import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  META_EXPERIMENTS,
  periodIndexFor,
  periodWindow,
  variantForPeriod,
} from "@/content/metaExperiments";

type ResultRow = {
  experiment_id: string;
  path: string;
  variant_id: string;
  period_index: number;
  period_start: string;
  period_end: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number | null;
  title: string | null;
  description: string | null;
  synced_at: string;
};

export const getMetaExperimentResults = createServerFn({ method: "GET" }).handler(async () => {
  const { requireAdminUnlockedServerSide } = await import("@/lib/admin-gate.server");
  await requireAdminUnlockedServerSide();
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("meta_experiment_results")
    .select("*")
    .order("period_start", { ascending: false })
    .limit(500);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ResultRow[];
});

export const syncMetaExperiments = createServerFn({ method: "POST" }).handler(async () => {
  const { requireAdminUnlockedServerSide } = await import("@/lib/admin-gate.server");
  await requireAdminUnlockedServerSide();
  const { runMetaAbSync } = await import("./api/public/hooks/meta-ab-sync");
  return runMetaAbSync();
});

export const Route = createFileRoute("/admin/meta-experiments/")({
  head: () => ({
    meta: [
      { title: "Meta A/B Experiments — Optimize Plus" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const { checkAdminUnlocked } = await import("@/lib/admin-gate.functions");
    await checkAdminUnlocked();
  },
  component: MetaExperimentsDashboard,
});

const pct = (n: number) => `${(n * 100).toFixed(2)}%`;

type VariantAgg = {
  variantId: string;
  title: string;
  description: string;
  clicks: number;
  impressions: number;
  ctr: number;
  positionSum: number;
  positionWeight: number;
  periods: number;
};

function MetaExperimentsDashboard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["meta-experiment-results"],
    queryFn: () => getMetaExperimentResults(),
    refetchInterval: 120_000,
  });
  const sync = useMutation({
    mutationFn: () => syncMetaExperiments(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["meta-experiment-results"] }),
  });

  const rows = data ?? [];

  return (
    <div className="container mx-auto py-10 space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Meta Title &amp; Description A/B Tests</h1>
          <p className="text-muted-foreground mt-1 max-w-3xl">
            Each page rotates between variants on a fixed cycle, so crawlers only ever see one
            version at a time. Search Console clicks, impressions, CTR and average position are
            pulled nightly for every completed period and attributed to the variant that was live.
          </p>
        </div>
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {sync.isPending ? "Syncing…" : "Sync from Search Console"}
        </button>
      </div>

      {sync.isError && (
        <p className="text-destructive text-sm">{(sync.error as Error).message}</p>
      )}
      {sync.isSuccess && (
        <p className="text-sm text-muted-foreground">
          Synced {sync.data.synced} period(s) from {sync.data.siteUrl}.
          {sync.data.errors.length > 0 && ` ${sync.data.errors.length} error(s): ${sync.data.errors.join("; ")}`}
        </p>
      )}
      {isLoading && <p>Loading…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}

      <div className="space-y-10">
        {META_EXPERIMENTS.map((exp) => {
          const expRows = rows.filter((r) => r.experiment_id === exp.id);
          const currentIndex = periodIndexFor(exp);
          const liveVariant = variantForPeriod(exp, currentIndex);
          const window = periodWindow(exp, Math.max(currentIndex, 0));

          const agg = new Map<string, VariantAgg>();
          for (const v of exp.variants) {
            agg.set(v.id, {
              variantId: v.id,
              title: v.title,
              description: v.description,
              clicks: 0,
              impressions: 0,
              ctr: 0,
              positionSum: 0,
              positionWeight: 0,
              periods: 0,
            });
          }
          for (const r of expRows) {
            const a = agg.get(r.variant_id);
            if (!a) continue;
            a.clicks += r.clicks;
            a.impressions += r.impressions;
            a.periods += 1;
            if (r.position != null && r.impressions > 0) {
              a.positionSum += r.position * r.impressions;
              a.positionWeight += r.impressions;
            }
          }
          const aggregates = [...agg.values()].map((a) => ({
            ...a,
            ctr: a.impressions > 0 ? a.clicks / a.impressions : 0,
            avgPosition: a.positionWeight > 0 ? a.positionSum / a.positionWeight : null,
          }));
          const complete = aggregates.filter((a) => a.impressions >= 100);
          const leader =
            complete.length === aggregates.length && aggregates.length > 1
              ? [...aggregates].sort((x, y) => y.ctr - x.ctr)[0]
              : null;

          return (
            <section key={exp.id} className="rounded-lg border p-5 space-y-4">
              <header className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold">{exp.label}</h2>
                  <p className="text-sm text-muted-foreground">
                    {exp.path} · {exp.cycleDays}-day cycle · period #{Math.max(currentIndex, 0) + 1}{" "}
                    ({window.start} → {window.end})
                  </p>
                </div>
                <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium">
                  Live now: {liveVariant.id}
                </span>
              </header>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-3">Variant</th>
                      <th className="py-2 pr-3">Title</th>
                      <th className="py-2 pr-3 text-right">Periods</th>
                      <th className="py-2 pr-3 text-right">Impressions</th>
                      <th className="py-2 pr-3 text-right">Clicks</th>
                      <th className="py-2 pr-3 text-right">CTR</th>
                      <th className="py-2 text-right">Avg. position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregates.map((a) => (
                      <tr key={a.variantId} className="border-t align-top">
                        <td className="py-2 pr-3 font-medium">
                          {a.variantId}
                          {leader?.variantId === a.variantId && (
                            <span className="ml-2 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                              leading
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 max-w-md">
                          <span className="block">{a.title}</span>
                          <span className="block text-xs text-muted-foreground">{a.description}</span>
                        </td>
                        <td className="py-2 pr-3 text-right">{a.periods}</td>
                        <td className="py-2 pr-3 text-right">{a.impressions.toLocaleString()}</td>
                        <td className="py-2 pr-3 text-right">{a.clicks.toLocaleString()}</td>
                        <td className="py-2 pr-3 text-right">{pct(a.ctr)}</td>
                        <td className="py-2 text-right">
                          {a.avgPosition == null ? "—" : a.avgPosition.toFixed(1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {expRows.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground">
                    Period-by-period history ({expRows.length})
                  </summary>
                  <table className="mt-3 w-full text-sm">
                    <thead className="text-left text-muted-foreground">
                      <tr>
                        <th className="py-1 pr-3">Period</th>
                        <th className="py-1 pr-3">Variant</th>
                        <th className="py-1 pr-3 text-right">Impr.</th>
                        <th className="py-1 pr-3 text-right">Clicks</th>
                        <th className="py-1 pr-3 text-right">CTR</th>
                        <th className="py-1 text-right">Pos.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...expRows]
                        .sort((a, b) => b.period_index - a.period_index)
                        .map((r) => (
                          <tr key={r.period_index} className="border-t">
                            <td className="py-1 pr-3">
                              {r.period_start} → {r.period_end}
                            </td>
                            <td className="py-1 pr-3">{r.variant_id}</td>
                            <td className="py-1 pr-3 text-right">{r.impressions.toLocaleString()}</td>
                            <td className="py-1 pr-3 text-right">{r.clicks.toLocaleString()}</td>
                            <td className="py-1 pr-3 text-right">{pct(r.ctr)}</td>
                            <td className="py-1 text-right">
                              {r.position == null ? "—" : r.position.toFixed(1)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </details>
              )}

              {!leader && (
                <p className="text-xs text-muted-foreground">
                  Needs at least 100 impressions per variant before a winner is called.
                </p>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
